import React, { useState, useEffect } from 'react';
import { Box, Grid, Link, Typography, Skeleton, IconButton, TextField } from '@mui/material';
import styled from '@emotion/styled';
import { EmailOutlined, CallOutlined } from '@mui/icons-material';
import Icon from '@mdi/react';
import { Edit, Check, Close } from '@mui/icons-material';
import { mdiWhatsapp } from '@mdi/js';
import { useDispatch, useSelector } from 'react-redux';
import { getDepartmentByUser } from '../redux/ducks/department';
import { useNavigate } from 'react-router-dom';
import { updateUser } from '../redux/slices/users';
import { useHasPermission } from '../utils/permissionUtils';

const BoxContainer = styled(Box)(({ theme}) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
    marginTop: theme.spacing(2),
    height: '100%',
}));

const BoxHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
}));

const BoxTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
    textAlign: 'left',
    marginRight: theme.spacing(2),
}));

const DataSet = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
}));

const DataSetItem = styled(Box)(({ theme })=>({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    width: '100%',
}));

const DataSetItemTitle = styled(Typography)(({ theme })=>({
    color: theme.palette.text.primary,
    textAlign: 'left',
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(0.5),
        fontSize: theme.typography.size.md,
    },
}));

const DataSetItemValue = styled(Typography)(({ theme })=>({
    color: theme.palette.text.primary,
    textAlign: 'left',
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: theme.typography.fontWeightMedium,
    [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(0.5),
        fontSize: theme.typography.size.md,
    },
}));

const DataSetItemLink = styled(Link)(({ theme })=>({
    color: theme.palette.text.primary,
    textAlign: 'left',
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: theme.typography.fontWeightMedium,
    cursor: 'pointer',
    [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(0.5),
        fontSize: theme.typography.size.md,
    },
}));

const DataSetItemSingle = styled(Box)(({ theme })=>({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(0.5),
        fontSize: theme.typography.size.md,
    },
}));

const ProfileContactBox = ( {user={}, isLoading=true, canSeeInternal=false} ) => {
    const profileUserIsInternal = user?.role?.type === 'Internal';
    const [department, setDepartment] = useState({});
    const [isDepartmentLoading, setDepartmentLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [userPhoneNumber, setUserPhoneNumber] = useState(user.contactMethods?.phoneNumber || '');
    const [userWhatsapp, setUserWhatsapp] = useState(user.contactMethods?.whatsapp || '');
    const [editedUserPhoneNumber, setEditedUserPhoneNumber] = useState(user.contactMethods?.phoneNumber || '');
    const [editedUserWhatsapp, setEditedUserWhatsapp] = useState(user.contactMethods?.whatsapp || '');
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const canEditUser = hasPermission('editUsers');
    const isSelf = user._id === useSelector(state => state.auth.userId);

    useEffect(() => {
        if (!canSeeInternal || !user?._id || !profileUserIsInternal) {
            setDepartment({});
            setDepartmentLoading(false);
            return;
        }
        setDepartmentLoading(true);
        dispatch(
            getDepartmentByUser(user._id, (dept) => {
                setDepartment(dept && typeof dept === 'object' && dept.name ? dept : {});
                setDepartmentLoading(false);
            })
        );
    }, [user?._id, user?.role?.type, profileUserIsInternal, canSeeInternal, dispatch]);

    const handleUserEdit = () => {
      setIsEditing(true);
      setEditedUserPhoneNumber(userPhoneNumber);
      setEditedUserWhatsapp(userWhatsapp);
    };

    const handleUserSave = () => {
      dispatch(
        updateUser({
          userId: user._id,
          userData: { contactMethods: { phoneNumber: editedUserPhoneNumber, whatsapp: editedUserWhatsapp } },
          callback: (response) => {
            if (response?.user) {
              setUserPhoneNumber(response.user.contactMethods?.phoneNumber || '');
              setUserWhatsapp(response.user.contactMethods?.whatsapp || '');
              setIsEditing(false);
            }
          },
        })
      );
    };

    const handleUserCancel = () => {
      setIsEditing(false);
      setEditedUserPhoneNumber(userPhoneNumber);
      setEditedUserWhatsapp(userWhatsapp);
    };

    return (
        <Grid item xs={12} md={4} lg={4}>
            <BoxContainer>
                <BoxHeader>
                    <BoxTitle variant="h5">Contact</BoxTitle>
                    {(isSelf || canEditUser) && (
                        <>
                            {isEditing ? (
                                <Box>
                                    <IconButton onClick={handleUserSave}>
                                        <Check />
                                    </IconButton>
                                    <IconButton onClick={handleUserCancel}>
                                        <Close />
                                    </IconButton>
                                </Box>
                            ) : (
                                <IconButton onClick={handleUserEdit}>
                                    <Edit />
                                </IconButton>
                            )}
                        </>
                    )}
                </BoxHeader>
                <DataSet>
                    <DataSetItem>
                        <DataSetItemSingle variant="body1">
                            <EmailOutlined />
                            {isLoading ? <Skeleton width={100} height={20} /> : (
                                <Link variant="body1" color="text.primary" href={`mailto:${user.email}`} target="_blank">
                                    {user.email}
                                </Link>
                            )}
                        </DataSetItemSingle>
                    </DataSetItem>
                    {(canSeeInternal || isSelf) && (
                        <>
                            <DataSetItem>
                                <DataSetItemSingle variant="body1">
                                    {(userPhoneNumber || isEditing) && <CallOutlined />}
                                    {isLoading ? <Skeleton width={100} height={20} /> : (
                                        isEditing ? (
                                            <TextField label='Phone Number' type="text" value={editedUserPhoneNumber} onChange={(e) => setEditedUserPhoneNumber(e.target.value)} />
                                        ) : (
                                            userPhoneNumber && (
                                                <Link variant="body1" color="text.primary" href={`tel:${userPhoneNumber}`} target="_blank">
                                                    {userPhoneNumber}
                                                </Link>
                                            )
                                        )
                                    )}
                                </DataSetItemSingle>
                            </DataSetItem>
                        <DataSetItem>
                            <DataSetItemSingle variant="body1">
                                {(userWhatsapp || isEditing) && <Icon path={mdiWhatsapp} size={1} />}
                                {isLoading ? <Skeleton width={100} height={20} /> : (
                                    isEditing ? (
                                        <TextField label='Whatsapp' type="text" value={editedUserWhatsapp} onChange={(e) => setEditedUserWhatsapp(e.target.value)} helperText="Include the country code, like +1 215 555 0134" />
                                    ) : (
                                        userWhatsapp && (
                                            <Link variant="body1" color="text.primary" href={`https://wa.me/${userWhatsapp}`} target="_blank">
                                                {userWhatsapp}
                                            </Link>
                                        )
                                    )
                                )}
                                </DataSetItemSingle>
                            </DataSetItem>
                        </>
                    )}
                    {profileUserIsInternal && department?.name && (
                        <>
                            <DataSetItem>
                                <DataSetItemTitle variant="body1">Department</DataSetItemTitle>
                                <DataSetItemValue variant="overline">{isDepartmentLoading ? <Skeleton width={100} /> : department?.name}</DataSetItemValue>
                            </DataSetItem>
                            <DataSetItem>
                            <DataSetItemTitle variant="body1">Manager</DataSetItemTitle>
                                <DataSetItemLink variant="overline" onClick={() => navigate(`/user/${department?.manager?._id}`)}>{isDepartmentLoading ? <Skeleton width={100} /> : department?.manager?.fullName}</DataSetItemLink>
                            </DataSetItem>
                        </>
                    )}
                </DataSet>
            </BoxContainer>
        </Grid>
    );
};

export default ProfileContactBox;